<template>
  <div class="plan-picker">
    <h2 class="plan-picker-title">{{ $t('onboarding.planPicker.title') }}</h2>
    <p class="plan-picker-subtitle">{{ $t('onboarding.planPicker.subtitle') }}</p>

    <div
      class="plan-cards"
      role="radiogroup"
      :aria-label="$t('onboarding.planPicker.groupLabel')"
    >
      <div
        v-for="(planCard, index) in planCards"
        :key="planCard.id"
        :ref="(el) => setCardRef(el, index)"
        class="plan-option"
        :class="{ selected: planCard.id === selectedPlan }"
        :data-plan="planCard.id"
        role="radio"
        :aria-checked="planCard.id === selectedPlan ? 'true' : 'false'"
        :tabindex="planCard.id === selectedPlan ? 0 : -1"
        @click="select(planCard.id)"
        @keydown.enter.prevent="select(planCard.id)"
        @keydown.space.prevent="select(planCard.id)"
        @keydown.right.prevent="moveSelection(index, 1)"
        @keydown.down.prevent="moveSelection(index, 1)"
        @keydown.left.prevent="moveSelection(index, -1)"
        @keydown.up.prevent="moveSelection(index, -1)"
      >
        <div class="plan-option-header">
          <span class="plan-option-name">{{ $t(planCard.nameKey) }}</span>
          <span v-if="planCard.id === selectedPlan" class="plan-option-badge">
            {{ $t('onboarding.planPicker.selected') }}
          </span>
        </div>
        <div class="plan-option-price" :data-price-state="planCard.priceState">
          <template v-if="planCard.priceState === 'known'">
            <span class="plan-option-amount">{{ planCard.price }}</span>
            <span class="plan-option-note">{{ $t(planCard.priceNoteKey) }}</span>
          </template>
          <!-- The store has reported no price yet. A radio takes its accessible
               name from its contents, so the skeleton carries the same line as
               visually-hidden text: a bare bar would delete the price from a
               selectable option's name. -->
          <span
            v-else-if="planCard.priceState === 'skeleton'"
            class="plan-option-price-skeleton"
            aria-busy="true"
          >
            <span class="visually-hidden">{{ $t('pricing.priceAtCheckout') }}</span>
          </span>
          <span v-else class="plan-option-price-pending">
            {{ $t('pricing.priceAtCheckout') }}
          </span>
        </div>
        <ul class="plan-option-features">
          <li v-for="feature in planCard.features" :key="feature.key">
            {{ $t(feature.key, feature.params) }}
          </li>
        </ul>
      </div>
    </div>

    <!-- OUTSIDE the radiogroup, deliberately. role="radio" is a
         presentational-children role, so a live region nested inside a card is
         stripped from the accessibility tree entirely.

         Empty except when the SELECTED card's price CHANGES UNDER A STABLE
         SELECTION. Two things are being contained. Scoping to the selected card
         stops an announcement storm: the price can flip on every app resume,
         because the iOS storefront re-queries on foreground. Ignoring selection
         changes stops a double announcement: moving the selection already makes
         the screen reader read the newly checked radio, whose accessible name
         contains the price, so mirroring it here would speak every arrow key
         twice.

         Announces the amount AND its note, which is the same pair of strings
         the visual price line already renders side by side, so a bare currency
         figure is never spoken without its billing period. -->
    <div class="visually-hidden" aria-live="polite">
      <template v-if="announcedPriceCard">
        <template v-if="announcedPriceCard.priceState === 'known'">
          {{ announcedPriceCard.price }} {{ $t(announcedPriceCard.priceNoteKey) }}
        </template>
        <template v-else>{{ $t('pricing.priceAtCheckout') }}</template>
      </template>
    </div>

    <button type="button" class="plan-picker-cta" @click="confirm">
      {{ $t('onboarding.planPicker.continueWith', { plan: $t(selectedNameKey) }) }}
    </button>

    <PlanLegalLinks />
  </div>
</template>

<script>
/*
 * The onboarding plan step, and nothing else.
 *
 * SELECTION ONLY, by decision (spec 2026-08-03, tradeoff 1). This component
 * owns no purchase machinery: no purchase button, no checkout launch, no store
 * call beyond reading a cached price. The wizard completes first and the host
 * launches checkout afterwards, which is what keeps the unskippable
 * static-backdrop onboarding modal from ever blocking on a payment sheet.
 *
 * It DOES read the payment strategy, and must (spec 2026-08-06). A price on a
 * card is a promise about what the tap will cost, so the display predicate has
 * to be the CHARGE predicate — the same conjunction launchPlanCheckout uses at
 * DashboardView.vue:388. Reading it here is a synchronous ref read and blocks
 * nothing; the iOS storefront race is handled by staying reactive and failing
 * closed, not by refusing to look. See monthlyPriceCard below.
 *
 * Why the prices differ per rail at all, and what the actual figures are:
 * context/pricing.md.
 *
 * That is also why this is NOT a variant of UpgradeOptions: that component is
 * purchase machinery (required onUpgrade prop, provider-locked upgrade paths)
 * and the two would share nothing but the catalog. What they DO share is
 * extracted: PlanLegalLinks, the pricing store, and iapUtils.
 */
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { pricingStore } from '@/store/pricing.js';
import { platformBilling } from '@/scripts/platformBilling.js';
import { iapUtils } from '@/scripts/iapUtils.js';
import { getEffectiveFreeLimits } from '@/composables/useUsageCounts.js';
import PlanLegalLinks from '@/components/PlanLegalLinks.vue';

const PLAN_IDS = ['free', 'foundation', 'fluency'];
const DEFAULT_PLAN = 'free';
// v1 offers the monthly cycle only; the yearly toggle stays on the Account
// upgrade surface, where a user comparing plans has room for it.
const BILLING_CYCLE = 'monthly';

// How long the card waits for the store to report a price before it gives up
// and says so in words.
//
// Keyed on a bounded window from MOUNT, deliberately not on iapUtils.isReady().
// isReady() is `initialized && store !== null`, and `initialized` is set only
// after `await store.initialize(...)` resolves, while the productUpdated handler
// that fills the price cache is registered BEFORE that await. So during store
// startup — exactly the window the skeleton exists for — isReady() is false and
// the cache is empty, which would route the first paint to the terminal line on
// every cold launch and then snap to a price; and once isReady() is true the
// price has usually already arrived, making the skeleton unreachable.
//
// One window handles "the store is still starting", "the store will never
// answer" (sideloaded APK, no signed-in store account) and "this one product
// never resolved" identically, which is right: from the picker's side they are
// indistinguishable and the remedy is the same.
export const UNKNOWN_PRICE_WINDOW_MS = 4000;

export default {
  name: 'OnboardingPlanPicker',
  components: { PlanLegalLinks },
  props: {
    // Canonical plan id: 'free' | 'foundation' | 'fluency'. Anything else falls
    // back to free rather than rendering a picker with nothing selected.
    modelValue: {
      type: String,
      default: DEFAULT_PLAN,
    },
    // The `users/{uid}` snapshot value. Only used to derive the free tier's
    // EFFECTIVE limits, so a launch-freebies account is told about the sample
    // it actually has instead of the catalog's 1 + 1.
    userData: {
      type: Object,
      default: null,
    },
  },
  emits: ['update:modelValue', 'confirm'],
  setup(props, { emit }) {
    const normalize = (planId) => (PLAN_IDS.includes(planId) ? planId : DEFAULT_PLAN);
    const selectedPlan = ref(normalize(props.modelValue));
    watch(
      () => props.modelValue,
      (planId) => {
        selectedPlan.value = normalize(planId);
      }
    );

    const freeLimits = computed(() => getEffectiveFreeLimits(props.userData));

    // Has the unknown-price window closed? Starts at mount; the timer is
    // cleared on unmount because the modal is torn down the instant the wizard
    // completes.
    //
    // The wall-clock deadline is the backstop, and it is not redundant. A
    // native WebView suspends or throttles timers while the app is backgrounded,
    // so a picker mounted just before the user switches away can come back with
    // its setTimeout still pending long past four seconds — which is exactly the
    // permanent-skeleton failure the terminal state exists to prevent. Settling
    // the window on visibilitychange bounds it in wall-clock time rather than in
    // foreground time. The deadline check is what keeps it a DEADLINE: a resume
    // inside the window must not skip the skeleton.
    const unknownPriceWindowElapsed = ref(false);
    let unknownPriceTimer = null;
    let unknownPriceDeadline = 0;
    const settleUnknownPriceWindow = () => {
      if (unknownPriceWindowElapsed.value) return;
      if (Date.now() >= unknownPriceDeadline) {
        unknownPriceWindowElapsed.value = true;
      }
    };
    onMounted(() => {
      unknownPriceDeadline = Date.now() + UNKNOWN_PRICE_WINDOW_MS;
      unknownPriceTimer = setTimeout(() => {
        unknownPriceWindowElapsed.value = true;
      }, UNKNOWN_PRICE_WINDOW_MS);
      document.addEventListener('visibilitychange', settleUnknownPriceWindow);
    });
    onUnmounted(() => {
      if (unknownPriceTimer !== null) {
        clearTimeout(unknownPriceTimer);
        unknownPriceTimer = null;
      }
      document.removeEventListener('visibilitychange', settleUnknownPriceWindow);
    });

    // The price of the rail that will actually charge, plus which of the three
    // render states that leaves the card in.
    //
    // The rail is decided by the SAME conjunction launchPlanCheckout uses
    // (DashboardView.vue:388): strategy `iap-only` AND a base plan id exists.
    // Matching only the strategy would let a plan shipped without iapBasePlans
    // advertise a store price and then charge through Stripe.
    //
    // Reactivity is the design, not an optimization. iosStorefrontCountry is a
    // ref and props.userData is replaced on every RTDB snapshot, and this runs
    // inside the planCards computed, so the card re-derives when either lands —
    // including the reverse direction, where a failed resume resets a resolved
    // storefront back to null. Anything that caches this, freezes it at mount,
    // or reads through a non-reactive intermediate breaks it silently.
    //
    // The country MUST go through getUserCountry: it carries the
    // navigator.language and "US" fallbacks, and passing
    // props.userData?.region?.country straight in sends undefined to
    // getPaymentStrategy, which normalizes to "" and forces every region-less
    // Android user onto iap-only.
    //
    // That fallback has a known residual window on ANDROID, and the direction
    // is the unfriendly one. Before the server has written region.country, an
    // en-US device in a non-Stripe country resolves to "US", so the card reads
    // $5 and then flips to the Play price when the country lands. Display and
    // charge still agree AT EVERY INSTANT — launchPlanCheckout calls the same
    // getUserCountry on the same snapshot, so a tap in that window really does
    // go to Stripe — and the card self-corrects reactively. Do NOT "fix" it by
    // failing closed to iap-only on a missing region: that would show the Play
    // price to a US Android user the checkout is about to send to Stripe, which
    // is the original bug pointed the other way. Closing it for real means
    // making BOTH call sites wait for the server-resolved country, which is a
    // payments-routing change, not a picker change.
    //
    // When the store is the charging rail and has reported nothing, there is no
    // true price to render: the USD catalog figure would be a lie to exactly the
    // users the store will charge in their own currency. Hence getCachedStorePrice
    // (which returns "" on a miss) rather than getProductPrice (which falls back
    // to the catalog literal and so cannot express "the store has said nothing").
    //
    // KNOWN LIMIT, pre-existing and not narrowed here: a cached store price can
    // be STALE in its own currency. productPrices is written only by the
    // productUpdated callback, and an iOS resume refreshes the storefront ref
    // without refetching products, so a mid-session Apple ID region switch (GBR
    // to DEU, both iap-only) can leave the last GBP figure on screen while
    // StoreKit charges EUR. The old isNativeApp() branch read the same cache and
    // had the identical exposure, and so does UpgradeOptions; invalidating the
    // cache on a storefront change is a shared-IAP-state fix, not a picker one.
    const monthlyPriceCard = (planId) => {
      const plan = pricingStore.plans[planId];
      const basePlanId = pricingStore.getBasePlanId(planId, BILLING_CYCLE);
      const strategy = platformBilling.getPaymentStrategy(
        platformBilling.getUserCountry(props.userData)
      );
      if (strategy !== 'iap-only' || !basePlanId) {
        return { price: `$${plan.monthlyPrice}`, priceState: 'known' };
      }
      const storePrice = iapUtils.getCachedStorePrice(basePlanId);
      if (storePrice) return { price: storePrice, priceState: 'known' };
      return {
        price: '',
        priceState: unknownPriceWindowElapsed.value ? 'pending' : 'skeleton',
      };
    };

    const paidFeatures = (planId) => {
      const plan = pricingStore.plans[planId];
      const features = [
        { key: 'pricing.chatsPerMonth', params: { count: plan.chatsPerMonth } },
        { key: 'pricing.lessonsPerMonth', params: { count: plan.lessonsPerMonth } },
      ];
      if (plan.multiLingual) {
        features.push({ key: 'pricing.multiLingualAccess', params: {} });
      }
      return features;
    };

    const planCards = computed(() => [
      {
        id: 'free',
        nameKey: pricingStore.plans.free.nameKey,
        price: `$${pricingStore.plans.free.price}`,
        // The free card charges nothing on any rail, so $0 is true everywhere
        // and it never enters the unknown-price states.
        priceState: 'known',
        priceNoteKey: 'pricing.foreverFree',
        features: [
          { key: 'pricing.freeChatsToStart', params: { count: freeLimits.value.chats } },
          { key: 'pricing.lifetimeLesson', params: { count: freeLimits.value.lessons } },
        ],
      },
      ...['foundation', 'fluency'].map((planId) => ({
        id: planId,
        nameKey: pricingStore.plans[planId].nameKey,
        ...monthlyPriceCard(planId),
        priceNoteKey: 'pricing.perMonth',
        features: paidFeatures(planId),
      })),
    ]);

    const selectedNameKey = computed(() => pricingStore.plans[selectedPlan.value].nameKey);

    // The card the live region is about. Falls back to the first card rather
    // than undefined so nothing downstream has to null-guard; selectedPlan is
    // normalized to a real plan id, so the fallback is unreachable in practice.
    const selectedPriceCard = computed(
      () =>
        planCards.value.find((planCard) => planCard.id === selectedPlan.value) ??
        planCards.value[0]
    );

    // What the live region actually holds: null (silent) unless the selected
    // card's price CHANGED while the selection stayed put.
    //
    // Binding the region straight to selectedPriceCard would double-announce
    // every arrow key: the screen reader already reads the newly checked
    // radio, and role="radio" takes its name from its contents, which include
    // the price. So a selection change clears the region instead of writing to
    // it, and only an asynchronous price arrival (the store cache warming, the
    // storefront resolving, the country landing) is announced.
    //
    // What is compared is the SENTENCE that would be spoken, not the render
    // state. `skeleton` and `pending` look different but read out identically,
    // so comparing states would say "Price shown at checkout" a second time the
    // moment the window elapsed, when nothing the user can hear had changed.
    const announcementKeyOf = (priceCard) =>
      priceCard.priceState === 'known'
        ? `known|${priceCard.price}|${priceCard.priceNoteKey}`
        : 'unknown';

    const announcedPriceCard = ref(null);
    watch(
      // The getter returns a fresh object every run, so the watcher fires on
      // every planCards re-derivation; the comparison that decides whether to
      // announce is done below, on fields, not by Vue's identity check.
      () => {
        const priceCard = selectedPriceCard.value;
        return {
          id: priceCard.id,
          price: priceCard.price,
          priceState: priceCard.priceState,
          priceNoteKey: priceCard.priceNoteKey,
          announcementKey: announcementKeyOf(priceCard),
        };
      },
      (next, previous) => {
        if (!previous || next.id !== previous.id) {
          announcedPriceCard.value = null;
          return;
        }
        if (next.announcementKey === previous.announcementKey) return;
        announcedPriceCard.value = next;
      }
    );

    const select = (planId) => {
      if (selectedPlan.value === planId) return;
      selectedPlan.value = planId;
      emit('update:modelValue', planId);
    };

    // Arrow keys move the selection, which is the standard radio-group
    // behaviour: the roving tabindex follows aria-checked.
    //
    // Focus HAS to travel with the selection. The keydown handler is bound per
    // card and closes over that card's index, so leaving focus behind would
    // make every further arrow press compute its neighbour from the ORIGINAL
    // card: a keyboard user could reach the second option and no further, and
    // the focus ring would sit on a card that is no longer checked.
    const cardRefs = ref([]);
    const setCardRef = (element, index) => {
      cardRefs.value[index] = element ?? null;
    };

    const moveSelection = (index, step) => {
      const next = (index + step + PLAN_IDS.length) % PLAN_IDS.length;
      select(PLAN_IDS[next]);
      cardRefs.value[next]?.focus?.();
    };

    const confirm = () => emit('confirm', selectedPlan.value);

    return {
      selectedPlan,
      selectedNameKey,
      announcedPriceCard,
      planCards,
      select,
      setCardRef,
      moveSelection,
      confirm,
    };
  },
};
</script>

<style scoped>
.plan-picker {
  display: flex;
  flex-direction: column;
  gap: var(--al-space-md);
}

.plan-picker-title {
  font-size: 1.375rem;
  line-height: 1.16;
  font-weight: 700;
  color: var(--al-text);
  margin: 0;
  text-align: center;
}

.plan-picker-subtitle {
  font-size: 0.875rem;
  line-height: 1.45;
  color: var(--al-text-muted);
  margin: 0;
  text-align: center;
}

.plan-cards {
  display: flex;
  flex-direction: column;
  gap: var(--al-space-sm);
}

.plan-option {
  background: var(--al-surface-raised);
  border: 1px solid var(--al-border);
  border-radius: var(--al-radius-md);
  padding: var(--al-space-md);
  cursor: pointer;
  transition: border-color var(--al-dur-short) var(--al-ease-move),
    background-color var(--al-dur-short) var(--al-ease-move),
    box-shadow var(--al-dur-short) var(--al-ease-move);
}

.plan-option:hover {
  border-color: var(--al-primary);
}

.plan-option:focus-visible {
  outline: 2px solid var(--al-primary);
  outline-offset: 2px;
}

.plan-option.selected {
  border-color: var(--al-primary);
  background: var(--al-primary-soft);
  box-shadow: var(--al-shadow-sm);
}

.plan-option-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--al-space-sm);
}

.plan-option-name {
  font-size: 1rem;
  font-weight: 700;
  color: var(--al-text);
}

.plan-option-badge {
  font-size: 0.75rem;
  font-weight: 650;
  line-height: 1.35;
  padding: var(--al-space-2xs) var(--al-space-sm);
  border-radius: var(--al-radius-pill);
  background: var(--al-primary);
  color: var(--al-white);
}

.plan-option-price {
  display: flex;
  align-items: baseline;
  gap: var(--al-space-xs);
  margin-top: var(--al-space-xs);
}

.plan-option-amount {
  font-size: 1.625rem;
  font-weight: 700;
  color: var(--al-text);
  font-variant-numeric: tabular-nums;
}

.plan-option-note {
  font-size: 0.875rem;
  color: var(--al-text-muted);
}

/* The store has not reported a price yet. Sized to the price line it stands in
   for so the card does not jump when the real figure lands. Matches the shimmer
   in SkeletonLoader. */
.plan-option-price-skeleton {
  display: inline-block;
  width: 6rem;
  height: 1.625rem;
  border-radius: var(--al-radius-sm);
  background: linear-gradient(
    90deg,
    var(--al-surface-sunken) 25%,
    var(--al-border) 50%,
    var(--al-surface-sunken) 75%
  );
  background-size: 200% 100%;
  animation: plan-price-shimmer 1.5s infinite;
}

@keyframes plan-price-shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

/* The terminal state: the store is not going to answer. Says so in words rather
   than shimmering forever on a modal the user cannot dismiss. */
.plan-option-price-pending {
  font-size: 1rem;
  font-weight: 650;
  line-height: 1.35;
  color: var(--al-text-muted);
}

.plan-option-features {
  list-style: none;
  padding: 0;
  margin: var(--al-space-sm) 0 0;
}

.plan-option-features li {
  display: flex;
  align-items: center;
  gap: var(--al-space-xs);
  padding: var(--al-space-2xs) 0;
  font-size: 0.875rem;
  color: var(--al-text);
}

/* Success-dark clears the 3:1 non-text contrast floor that the warm accent
   misses, and matches the "included feature" marker in UpgradeOptions. */
.plan-option-features li::before {
  content: "✓";
  color: var(--al-success-dark);
  font-weight: 700;
}

.plan-picker-cta {
  width: 100%;
  min-height: 48px;
  padding: var(--al-space-md);
  border: none;
  border-radius: var(--al-radius-md);
  background-color: var(--al-primary);
  color: var(--al-white);
  font-weight: 700;
  cursor: pointer;
  transition: background-color var(--al-dur-short) var(--al-ease-move);
}

.plan-picker-cta:hover {
  background-color: var(--al-primary-hover);
}

.plan-picker-cta:focus-visible {
  outline: 2px solid var(--al-primary);
  outline-offset: 2px;
}

@media (prefers-reduced-motion: reduce) {
  .plan-option,
  .plan-picker-cta {
    transition: none;
  }

  /* Motion is for orientation, not decoration. */
  .plan-option-price-skeleton {
    animation: none;
    background: var(--al-surface-sunken);
  }
}
</style>
